import type { QueryClient } from '@tanstack/react-query'
import type { Everywhere } from './everywhere'

/*
 * ASK AGAIN UNTIL THE ROSTER AGREES — owner, 2026-08-23: create / delete agent 都不會
 * 立刻反應在 side panel 上面.
 *
 * Not a latency problem, which is why one more invalidate would not have fixed it.
 * Measured on app-dev against all four paired machines: the whole fan-out answers in
 * 547ms, and after /agent/create the edge reports the new session as an agent at
 * 646ms — while `createAgent` itself returns at 394ms.
 *
 * So the single invalidate fired on success goes out at 394ms and lands around 644ms,
 * right on top of the moment the CLI becomes detectable. Win the race and the row
 * appears; lose it by a few milliseconds and the machine truthfully answers that the
 * session is a plain TERMINAL, because the CLI has not started yet — and nothing ever
 * asks again. There is no interval on ['agents']; it refetches on invalidation, on
 * window focus, and on a devices frame. The screen then sits on an answer that was
 * correct when given and wrong a moment later.
 *
 * A fixed delay would be the same coin flip moved later, so this re-asks until what
 * the caller is waiting for is actually true, and gives up after a bounded number of
 * tries rather than spinning: if the expectation never holds, the roster is still
 * correct — it just disagrees with what we predicted, and the last answer is the
 * machine's, which is the one to keep.
 *
 * `refetchQueries` with `type: 'all'`, not `invalidateQueries`. Both of those default
 * to touching only ACTIVE queries — ones something is currently observing — and the
 * sheet that triggers a create covers the rail that observes this one. So the default
 * would have re-asked nothing at exactly the moment it matters, which is the same
 * shape as the bug it is here to fix.
 */
export async function settleRoster(
  qc: QueryClient,
  holds: (e: Everywhere) => boolean,
  { tries = 6, gapMs = 600 }: { tries?: number; gapMs?: number } = {},
): Promise<boolean> {
  for (let i = 0; i < tries; i++) {
    /*
     * The refetch is kicked, not awaited to completion: the rail's query now
     * publishes each machine's share into the cache AS IT LANDS (Sidebar), so
     * the condition can hold seconds before the slowest machine finishes timing
     * out. Waiting for the whole sweep was most of the ten seconds the owner
     * measured on create (2026-08-30). The poll below reads the same cache the
     * screen reads, so "the sheet moved on" and "the row is visible" agree.
     */
    const sweep = qc.refetchQueries({ queryKey: ['agents'], type: 'all' })
    let settled = false
    void sweep.then(() => (settled = true))
    for (;;) {
      const e = qc.getQueryData<Everywhere>(['agents'])
      if (e && holds(e)) return true
      if (settled) break
      await new Promise((r) => setTimeout(r, 50))
    }
    const e = qc.getQueryData<Everywhere>(['agents'])
    if (e && holds(e)) return true
    if (i < tries - 1) await new Promise((r) => setTimeout(r, gapMs))
  }
  return false
}

/** The session is listed as a live agent (not merely as a surviving terminal). */
export const agentPresent = (session: string) => (e: Everywhere) =>
  e.agents.some((a) => a.session === session)

/** A ＋ → Terminal session is listed on the machine that made it (a stamp-less row: the one machine). */
export const terminalPresent = (session: string, device: string) => (e: Everywhere) =>
  e.terminals.some((t) => t.session === session && (!t.device_id || t.device_id === device))

/** The session is gone from BOTH columns — what deleting an agent now means. */
export const sessionGone = (session: string) => (e: Everywhere) =>
  !e.agents.some((a) => a.session === session) &&
  !e.terminals.some((t) => (t as { session?: string }).session === session)

/**
 * The just-created agent's row, out of the settled roster — the argument to
 * openAgent() that finishes the create flow.
 *
 * Owner, 2026-08-24: 當創造一個新的 Agent 之後應該要直接進入該 Agent 的 session
 * 而非讓使用者自己從 side panel 來點擊進入. The project half of the same sheet has
 * carried this contract for a while ("straight into the room that was just made");
 * the agent half could not act at create-return time because everything opening
 * needs — the channel hash, the device stamp — is minted on the machine and only
 * arrives with the roster row. So the row is picked here, after settleRoster.
 *
 * Matched by session AND machine first: a session name is not unique across the
 * fleet (web-train-board exists on two machines today), and the sheet knows which
 * machine it asked. The name-only fallback covers rows that predate the fan-out
 * and carry no stamp — with one machine there is only one thing the name can mean.
 */
export const newlyCreatedRow = (e: Everywhere, session: string, device: string) =>
  e.agents.find((a) => a.session === session && a.device_id === device) ??
  e.agents.find((a) => a.session === session && !a.device_id)
